/**
 * Runtime configuration loader for Self-Publishing Wizard
 * Loads config from /config.json served as static asset, fallback to build-time env vars
 */

import {CONFIG_LOAD_TIMEOUT} from '@/constants/timeouts';
import {instrumentationProvider} from '@/instrumentation'
import {isTruthy} from "@/lib/utils.ts";

// [SEL-6] Runtime config with Sentry settings block (DSN, enabled, environment)
export interface AppConfig {
  version?: string;
  domain?: string;
  environment?: string;
  loaded?: boolean;
  debug?: boolean;
  debug_auth?: boolean;
  debug_signup?: boolean;
  maintenance?: boolean;
  trace?: boolean;
  sentry: {
    enabled: boolean;
    dsn: string;
    environment?: string;
    send_errors?: boolean;
  };
  posthog: {
    enabled: boolean;
    host: string;
    key?: string;
    capture_exceptions?: boolean;
  };
  supabase: {
    url: string;
    publishableKey: string;
    projectId: string;
  };
}

class RuntimeConfigLoader {
  config: AppConfig | null = null;
  loadPromise: Promise<AppConfig> | null = null;
  private state: 'idle' | 'loading' | 'loaded' = 'idle';

  /**
   * Load configuration asynchronously with 5-second timeout
   * Falls back to build-time env vars if config.json is not available or times out
   */
  async load(): Promise<AppConfig> {
    // Return cached config if already loaded
    if (this.config) {
      return this.config;
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading process with CONFIG_LOAD_TIMEOUT
    this.state = 'loading';

    // Create AbortController with timeout from constants
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG_LOAD_TIMEOUT);

    this.loadPromise = fetch('/config.json', {signal: controller.signal})
      .then(response => {
        clearTimeout(timeoutId);
        // if (this.config?.debug) console.log(`[Config.load] response: ${response.status} ${response.statusText}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(config => {
        if (config?.debug) console.log(`[Config.load] config: ${config.supabase.projectId}`);
        config.loaded = true;
        this.config = config;
        this.state = 'loaded';
        return config;
      })
      .catch(error => {
        clearTimeout(timeoutId);
        const isAbortError = error.name === 'AbortError';
        if (isAbortError) {
          console.log(`[Config.load] Timeout after ${CONFIG_LOAD_TIMEOUT}ms, using build-time fallback`);
        } else {
          console.log('[Config.load] Failed to load, using build-time fallback:', error.message);
        }
        this.config = this.createFromEnv();
        this.state = 'loaded';
        return this.config;
      });

    return this.loadPromise;
  }

  /**
   * Get config synchronously (throws if not loaded yet)
   */
  get(): AppConfig {
    if (this.config == null) {
      throw new Error('Config not loaded yet. Call runtimeConfig.load() first.');
    }
    return this.config;
  }

  /**
   * Clear cached config (for testing)
   */
  clear(): void {
    this.config = null;
    this.loadPromise = null;
    this.state = 'idle';
  }

  createFromEnv(): AppConfig {
    const config: AppConfig = {
      version: import.meta.env.VITE_VERSION || 'development',
      environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      loaded: false,
      debug: isTruthy(import.meta.env.DEBUG) || isTruthy(import.meta.env.VITE_DEBUG),
      debug_auth: isTruthy(import.meta.env.TEST_DEBUG_AUTH) || isTruthy(import.meta.env.VITE_DEBUG_AUTH),
      debug_signup: isTruthy(import.meta.env.TEST_DEBUG_SIGNUP) || isTruthy(import.meta.env.VITE_DEBUG_SIGNUP),
      sentry: {
        enabled: instrumentationProvider === "sentry",
        dsn: import.meta.env.VITE_SENTRY_DSN || 'https://5cce43edebb2f199ef4bf70ba78235eb@o4510267491352577.ingest.us.sentry.io/4510267492139008'
      },
      posthog: {
        enabled: instrumentationProvider === "posthog",
        host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
        key: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
      },
      supabase: {
        url: import.meta.env.VITE_SUPABASE_URL || '',
        publishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '',
        projectId: import.meta.env.VITE_SUPABASE_PROJECT_ID || '',
      }
    };

    // Validate required config
    if (!config.supabase.url || !config.supabase.publishableKey) {
      throw new Error('Missing required Supabase configuration. Check /config.json or VITE_ environment variables.');
    }

    return config;
  }
}

// Export singleton instance
export const runtimeConfig = new RuntimeConfigLoader();

/**
 * Get application configuration synchronously
 */
export function getAppConfigSync(): AppConfig {
  return runtimeConfig.get();
}
